import { normalizeFacilityLabel } from "@/features/hotels/utils/facilities/normalization";
import type {
  FacilityCategoryKey,
  FacilityCategoryMeta,
  FacilityTaxonomyItem,
} from "@/features/hotels/utils/facilities/types";

export const FACILITY_CATEGORIES: Record<
  FacilityCategoryKey,
  FacilityCategoryMeta
> = {
  "core-essentials": {
    key: "core-essentials",
    label: "Core Essentials",
  },
  accessibility: {
    key: "accessibility",
    label: "Accessibility",
  },
  safety: { key: "safety", label: "Safety" },
  leisure: { key: "leisure", label: "Leisure" },
  dining: { key: "dining", label: "Dining" },
  service: { key: "service", label: "Service" },
  sports: { key: "sports", label: "Sports" },
  "core-support": {
    key: "core-support",
    label: "Core Support",
  },
  wellness: { key: "wellness", label: "Wellness" },
  family: { key: "family", label: "Family" },
  business: { key: "business", label: "Business" },
  entertainment: {
    key: "entertainment",
    label: "Entertainment",
  },
  secondary: { key: "secondary", label: "Secondary" },
  policies: { key: "policies", label: "Policies" },
  admin: { key: "admin", label: "Admin" },
  extra: { key: "extra", label: "Extra" },
};

export const FACILITY_CATEGORY_ORDER: FacilityCategoryKey[] = [
  "core-essentials",
  "dining",
  "accessibility",
  "safety",
  "wellness",
  "sports",
  "leisure",
  "entertainment",
  "family",
  "business",
  "service",
  "secondary",
  "core-support",
  "policies",
  "admin",
  "extra",
];

const createTaxonomyItem = (
  item: Omit<FacilityTaxonomyItem, "normalizedLabel">,
): FacilityTaxonomyItem => ({
  ...item,
  normalizedLabel: normalizeFacilityLabel(item.label),
});

export const FACILITY_TAXONOMY: FacilityTaxonomyItem[] = [
  createTaxonomyItem({
    key: "wifi",
    label: "Wi-fi",
    rank: 1,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "air-conditioning-public-areas",
    label: "Air conditioning in public areas",
    rank: 2,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "24-hour-reception",
    label: "24-hour reception",
    rank: 3,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "towels-and-bed-linen",
    label: "Towels and bed linen",
    rank: 4,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "lift-access",
    label: "Lift access",
    rank: 5,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "car-park",
    label: "Car park",
    rank: 6,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "secure-parking",
    label: "Secure parking",
    rank: 7,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "valet-parking",
    label: "Valet parking",
    rank: 8,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "wheelchair-accessible",
    label: "Wheelchair-accessible",
    rank: 9,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "room-service",
    label: "Room service",
    rank: 10,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "laundry-service",
    label: "Laundry service",
    rank: 11,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "luggage-room",
    label: "Luggage room",
    rank: 12,
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "airport-shuttle",
    label: "Airport Shuttle",
    rank: 13,
    categoryKey: "accessibility",
  }),
  createTaxonomyItem({
    key: "car-hire",
    label: "Car hire",
    rank: 15,
    categoryKey: "accessibility",
  }),
  createTaxonomyItem({
    key: "gym",
    label: "Gym",
    rank: 23,
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "restaurant",
    label: "Restaurant",
    rank: 34,
    categoryKey: "dining",
  }),
  createTaxonomyItem({
    key: "swimming-pool",
    label: "Outdoor freshwater pool",
    rank: 29,
    categoryKey: "leisure",
  }),
  createTaxonomyItem({
    key: "breakfast",
    label: "Breakfast room",
    rank: 35,
    categoryKey: "dining",
  }),
  createTaxonomyItem({
    key: "multilingual-staff",
    label: "Multilingual staff",
    rank: 17,
    categoryKey: "accessibility",
  }),
  createTaxonomyItem({
    key: "24-hour-security",
    label: "24-hour security",
    rank: 19,
    categoryKey: "safety",
  }),
  createTaxonomyItem({
    key: "spa-centre",
    label: "Spa centre",
    rank: 24,
    categoryKey: "wellness",
  }),
  createTaxonomyItem({
    key: "sauna",
    label: "Sauna",
    rank: 25,
    categoryKey: "wellness",
  }),
  createTaxonomyItem({
    key: "bar",
    label: "Bar",
    rank: 36,
    categoryKey: "dining",
  }),
  createTaxonomyItem({
    key: "concierge",
    label: "Concierge",
    rank: 40,
    categoryKey: "service",
  }),
  createTaxonomyItem({
    key: "babysitting-service",
    label: "Babysitting service",
    rank: 45,
    categoryKey: "family",
  }),
  createTaxonomyItem({
    key: "children-playground",
    label: "Children playground",
    rank: 46,
    categoryKey: "family",
  }),
  createTaxonomyItem({
    key: "business-centre",
    label: "Business centre",
    rank: 49,
    categoryKey: "business",
  }),
  createTaxonomyItem({
    key: "meeting-room",
    label: "Meeting room",
    rank: 50,
    categoryKey: "business",
  }),
  createTaxonomyItem({
    key: "casino",
    label: "Casino",
    rank: 58,
    categoryKey: "entertainment",
  }),
  createTaxonomyItem({
    key: "nightclub",
    label: "Nightclub",
    rank: 59,
    categoryKey: "entertainment",
  }),
  createTaxonomyItem({
    key: "electric-kettle",
    label: "Electric kettle",
    rank: 71,
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "garage",
    label: "Garage",
    rank: 81,
    categoryKey: "core-support",
  }),
  createTaxonomyItem({
    key: "tennis",
    label: "Tennis",
    rank: 106,
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "small-pets-allowed",
    label: "Small pets allowed",
    rank: 117,
    categoryKey: "policies",
  }),
  createTaxonomyItem({
    key: "check-in-check-out-key-collection",
    label: "Check-in / Check-out / Key Collection",
    rank: 120,
    categoryKey: "admin",
  }),
  createTaxonomyItem({
    key: "central-heating",
    label: "Central heating",
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "internet-access",
    label: "Internet access",
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "disability-friendly-bathroom",
    label: "Disability-friendly bathroom",
    categoryKey: "core-essentials",
  }),
  createTaxonomyItem({
    key: "balcony",
    label: "Balcony",
    categoryKey: "leisure",
  }),
  createTaxonomyItem({
    key: "extra-beds-on-demand",
    label: "Extra beds on demand",
    categoryKey: "service",
  }),
  createTaxonomyItem({
    key: "towel-menu",
    label: "Towel menu",
    categoryKey: "service",
  }),
  createTaxonomyItem({
    key: "children-share-bed",
    label: "Children share the bed with parents",
    categoryKey: "family",
  }),
  createTaxonomyItem({
    key: "cot-on-demand",
    label: "Cot on demand",
    categoryKey: "family",
  }),
  createTaxonomyItem({
    key: "smoking-rooms",
    label: "Smoking rooms",
    categoryKey: "policies",
  }),
  createTaxonomyItem({
    key: "hairdryer",
    label: "Hairdryer",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "cable-tv",
    label: "Cable TV",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "carpeted-floors",
    label: "Carpeted floors",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "dishwasher",
    label: "Dishwasher",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "fridge",
    label: "Fridge",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "ironing-set",
    label: "Ironing set",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "kitchen",
    label: "Kitchen",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "kitchenette",
    label: "Kitchenette",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "kitchen-utensils",
    label: "Kitchen utensils",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "make-up-mirror",
    label: "Make-up mirror",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "microwave",
    label: "Microwave",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "plates-and-cups",
    label: "plates & cups",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "tea-and-coffee-making-facilities",
    label: "Tea and coffee making facilities",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "toiletries",
    label: "Toiletries",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "washing-machine",
    label: "Washing machine",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "safe",
    label: "Safe",
    categoryKey: "safety",
  }),
  createTaxonomyItem({
    key: "pillow-menu",
    label: "Pillow menu",
    categoryKey: "service",
  }),
  createTaxonomyItem({
    key: "wake-up-service",
    label: "Wake-up service",
    categoryKey: "service",
  }),
  createTaxonomyItem({
    key: "pay-movies",
    label: "Pay movies",
    categoryKey: "entertainment",
  }),
  createTaxonomyItem({
    key: "220v-power-supply",
    label: "220V power supply",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "alarm-clock",
    label: "Alarm clock",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "background-music",
    label: "Background music",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "bathrobes",
    label: "Bathrobes",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "bathroom",
    label: "Bathroom",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "shared-bathroom",
    label: "Shared bathroom",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "bathtub",
    label: "Bathtub",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "desk",
    label: "Desk",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "direct-dial-telephone",
    label: "Direct dial telephone",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "individually-adjustable-air-conditioning",
    label: "Individually adjustable air conditioning",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "individually-adjustable-heating",
    label: "Individually adjustable heating",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "mini-fridge",
    label: "Mini fridge",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "minibar",
    label: "Minibar",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "plug-adaptor",
    label: "Plug adaptor",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "radio",
    label: "Radio",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "shower",
    label: "Shower",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "slippers",
    label: "Slippers",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "tv",
    label: "TV",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "bicycle-hire-service",
    label: "Bicycle hire service",
    categoryKey: "accessibility",
  }),
  createTaxonomyItem({
    key: "garden",
    label: "Garden",
    categoryKey: "leisure",
  }),
  createTaxonomyItem({
    key: "hotel-safe",
    label: "Hotel safe",
    categoryKey: "safety",
  }),
  createTaxonomyItem({
    key: "shop",
    label: "Shop",
    categoryKey: "service",
  }),
  createTaxonomyItem({
    key: "late-check-out",
    label: "Late Check-out",
    categoryKey: "admin",
  }),
  createTaxonomyItem({
    key: "check-in-hour",
    label: "Check-in hour",
    categoryKey: "admin",
  }),
  createTaxonomyItem({
    key: "check-out-hour",
    label: "Check-out hour",
    categoryKey: "admin",
  }),
  createTaxonomyItem({
    key: "hot-tub",
    label: "Hot tub",
    categoryKey: "leisure",
  }),
  createTaxonomyItem({
    key: "housekeeping",
    label: "Housekeeping",
    categoryKey: "service",
  }),
  createTaxonomyItem({
    key: "centrally-regulated-air-conditioning",
    label: "Centrally regulated air conditioning",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "fan",
    label: "Fan",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "satellite-tv",
    label: "Satellite TV",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "telephone-in-the-bathroom",
    label: "Telephone in the bathroom",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "towels-and-bed-linen-at-surcharge",
    label: "Towels and bed linen at surcharge",
    categoryKey: "policies",
  }),
  createTaxonomyItem({
    key: "trouser-press",
    label: "Trouser press",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "smoke-detector",
    label: "Smoke detector",
    categoryKey: "safety",
  }),
  createTaxonomyItem({
    key: "bellboy-service",
    label: "Bellboy service",
    categoryKey: "service",
  }),
  createTaxonomyItem({
    key: "cloakroom",
    label: "Cloakroom",
    categoryKey: "service",
  }),
  createTaxonomyItem({
    key: "currency-exchange-facilities",
    label: "Currency exchange facilities",
    categoryKey: "service",
  }),
  createTaxonomyItem({
    key: "newspaper-stand",
    label: "Newspaper stand",
    categoryKey: "service",
  }),
  createTaxonomyItem({
    key: "supermarket",
    label: "Supermarket",
    categoryKey: "service",
  }),
  createTaxonomyItem({
    key: "transfer-service",
    label: "Transfer service",
    categoryKey: "accessibility",
  }),
  createTaxonomyItem({
    key: "bicycle-storage",
    label: "Bicycle storage",
    categoryKey: "accessibility",
  }),
  createTaxonomyItem({
    key: "dining-area",
    label: "Dining Area",
    categoryKey: "dining",
  }),
  createTaxonomyItem({
    key: "clothes-dryer",
    label: "Clothes dryer",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "mobile-phone-coverage",
    label: "Mobile phone coverage",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "wired-internet",
    label: "Wired Internet",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "turndown-service",
    label: "Turndown service",
    categoryKey: "service",
  }),
  createTaxonomyItem({
    key: "newspapers",
    label: "Newspapers",
    categoryKey: "service",
  }),
  createTaxonomyItem({
    key: "library",
    label: "Library",
    categoryKey: "entertainment",
  }),
  createTaxonomyItem({
    key: "sun-terrace",
    label: "Sun terrace",
    categoryKey: "leisure",
  }),
  createTaxonomyItem({
    key: "terrace",
    label: "Terrace",
    categoryKey: "leisure",
  }),
  createTaxonomyItem({
    key: "toaster",
    label: "Toaster",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "smoking-area",
    label: "Smoking area",
    categoryKey: "policies",
  }),
  createTaxonomyItem({
    key: "cycling-mountain-biking",
    label: "Cycling / mountain biking",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "diving",
    label: "Diving",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "fitness",
    label: "Fitness",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "golf",
    label: "Golf",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "pedal-boating",
    label: "Pedal boating",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "squash",
    label: "Squash",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "table-tennis",
    label: "Table tennis",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "pub",
    label: "Pub",
    categoryKey: "dining",
  }),
  createTaxonomyItem({
    key: "fireplace",
    label: "Fireplace",
    categoryKey: "leisure",
  }),
  createTaxonomyItem({
    key: "waterpark",
    label: "Waterpark",
    categoryKey: "leisure",
  }),
  createTaxonomyItem({
    key: "childrens-swimming-area",
    label: "Children's swimming area",
    categoryKey: "leisure",
  }),
  createTaxonomyItem({
    key: "medical-service",
    label: "Medical service",
    categoryKey: "service",
  }),
  createTaxonomyItem({
    key: "partners-bike-shop-workshop",
    label: "Partner's bike shop/workshop",
    categoryKey: "service",
  }),
  createTaxonomyItem({
    key: "key-collection",
    label: "Key Collection",
    categoryKey: "admin",
  }),
  createTaxonomyItem({
    key: "local-and-international-calls",
    label: "Local and international calls",
    categoryKey: "secondary",
  }),
  createTaxonomyItem({
    key: "non-smoking-area",
    label: "Non-smoking area",
    categoryKey: "policies",
  }),
  createTaxonomyItem({
    key: "highchairs",
    label: "Highchairs",
    categoryKey: "dining",
  }),
  createTaxonomyItem({
    key: "banquet-hall",
    label: "Banquet hall",
    categoryKey: "dining",
  }),
  createTaxonomyItem({
    key: "air-conditioning-in-restaurant",
    label: "Air conditioning in Restaurant",
    categoryKey: "dining",
  }),
  createTaxonomyItem({
    key: "24h-dining-cafe",
    label: "24h dining café",
    categoryKey: "dining",
  }),
  createTaxonomyItem({
    key: "rooftop-bar",
    label: "Rooftop bar",
    categoryKey: "dining",
  }),
  createTaxonomyItem({
    key: "cafe",
    label: "Café",
    categoryKey: "dining",
  }),
  createTaxonomyItem({
    key: "conference-room",
    label: "Conference room",
    categoryKey: "business",
  }),
  createTaxonomyItem({
    key: "projector",
    label: "Projector",
    categoryKey: "business",
  }),
  createTaxonomyItem({
    key: "conference-hostess",
    label: "Conference hostess",
    categoryKey: "business",
  }),
  createTaxonomyItem({
    key: "photocopier",
    label: "Photocopier",
    categoryKey: "business",
  }),
  createTaxonomyItem({
    key: "printer",
    label: "Printer",
    categoryKey: "business",
  }),
  createTaxonomyItem({
    key: "audio-visual-equipment-rental",
    label: "Audio-visual equipment rental",
    categoryKey: "business",
  }),
  createTaxonomyItem({
    key: "theatre-auditorium",
    label: "Theatre / auditorium",
    categoryKey: "entertainment",
  }),
  createTaxonomyItem({
    key: "game-room",
    label: "Game room",
    categoryKey: "entertainment",
  }),
  createTaxonomyItem({
    key: "steam-bath",
    label: "Steam bath",
    categoryKey: "wellness",
  }),
  createTaxonomyItem({
    key: "massage",
    label: "Massage",
    categoryKey: "wellness",
  }),
  createTaxonomyItem({
    key: "spa-treatments",
    label: "Spa treatments",
    categoryKey: "wellness",
  }),
  createTaxonomyItem({
    key: "turkish-bath-hamam",
    label: "Turkish bath (hamam)",
    categoryKey: "wellness",
  }),
  createTaxonomyItem({
    key: "banana-boating",
    label: "Banana boating",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "waterskiing",
    label: "Waterskiing",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "motor-board-ride",
    label: "Motor board ride",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "windsurfing",
    label: "Windsurfing",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "sailing",
    label: "Sailing",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "catamaran-sailing",
    label: "Catamaran sailing",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "canoeing",
    label: "Canoeing",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "aerobics",
    label: "Aerobics",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "archery",
    label: "Archery",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "basketball",
    label: "Basketball",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "volleyball",
    label: "Volleyball",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "billiards",
    label: "Billiards",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "mini-golf",
    label: "Mini golf",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "surfing",
    label: "Surfing",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "paddle-tennis",
    label: "Paddle tennis",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "badminton",
    label: "Badminton",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "darts",
    label: "Darts",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "putting-green",
    label: "Putting green",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "skiing",
    label: "Skiing",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "tobogganing",
    label: "Tobogganing",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "golf-practice-facility",
    label: "Golf practice facility",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "aqua-fit",
    label: "Aqua fit",
    categoryKey: "sports",
  }),
  createTaxonomyItem({
    key: "living-room",
    label: "Living room",
    categoryKey: "extra",
    withValue: true,
  }),
  createTaxonomyItem({
    key: "number-of-bedrooms",
    label: "Number of bedrooms",
    categoryKey: "extra",
    withValue: true,
  }),
  createTaxonomyItem({
    key: "room-size-sqm",
    label: "Room size (sqm)",
    categoryKey: "extra",
    withValue: true,
  }),
];

export const facilityTaxonomyByNormalizedLabel = new Map(
  FACILITY_TAXONOMY.map((item) => [item.normalizedLabel, item]),
);

export const MAX_COMMON_FACILITIES = 4;
export const COMMON_FACILITY_ICON_KEYS = new Set<string>([
  "wifi",
  "air-conditioning-public-areas",
  "24-hour-reception",
  "towels-and-bed-linen",
  "lift-access",
  "car-park",
  "secure-parking",
  "valet-parking",
  "wheelchair-accessible",
  "room-service",
  "laundry-service",
  "luggage-room",
  "airport-shuttle",
  "car-hire",
  "gym",
  "restaurant",
  "swimming-pool",
  "breakfast",
]);
export const COMMON_FACILITY_PRIORITY = [
  "air-conditioning-public-areas",
  "restaurant",
  "swimming-pool",
  "24-hour-reception",
] as const;
export const COMMON_FACILITY_PRIORITY_SET = new Set<string>(
  COMMON_FACILITY_PRIORITY,
);
